import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
 HiOutlinePlus,
 HiOutlineExclamation,
 HiOutlineChevronLeft,
} from "react-icons/hi";
import {
 fetchAssessmentYears,
 fetchAssessmentYearById,
 createAssessmentYear,
 updateAssessmentYear,
 deleteAssessmentYear,
 fetchHolidays,
} from "../services/masterDataApi";
import Toast from "../components/Toast";
import { useUserRole } from "../hooks/useUserRole";

function AssessmentYears() {
 const { canManageAssessmentYears } = useUserRole();
 const [toast, setToast] = useState(null);
 const [years, setYears] = useState([]);
 const [loading, setLoading] = useState(true);
 /* ── views: "list" | "form" — backed by the URL so a refresh in edit mode
    reopens the same assessment year instead of dropping back to the list ── */
 const [searchParams, setSearchParams] = useSearchParams();
 const view = searchParams.get("view") || "list";
 const urlEditId = searchParams.get("edit");
 const setView = useCallback((v, params = {}) => {
  if (v === "list") {
   setSearchParams({});
  } else if (v === "form" && params.edit) {
   setSearchParams({ view: "form", edit: params.edit });
  } else {
   setSearchParams({ view: v });
  }
 }, [setSearchParams]);

 // Form state
 const [editingYear, setEditingYear] = useState(null);
 const [form, setForm] = useState({ name: "", start_date: "", end_date: "", is_active: false });
 const [formError, setFormError] = useState("");

 // Delete state
 const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
 const [deleteHolidayCount, setDeleteHolidayCount] = useState(0);
 const [deleteLoading, setDeleteLoading] = useState(false);

 // ── Load assessment years ──
 const loadYears = useCallback(async () => {
  setLoading(true);
  try {
   const res = await fetchAssessmentYears();
   setYears(res.data || []);
  } catch (err) {
   console.error("Failed to load assessment years", err);
   setToast({ type: "error", message: "Failed to load assessment years" });
  } finally {
   setLoading(false);
  }
 }, []);

 useEffect(() => { loadYears(); }, [loadYears]);

 // ── Open create form ──
 const openCreate = () => {
  setEditingYear(null);
  setForm({ name: "", start_date: "", end_date: "", is_active: false });
  setFormError("");
  setView("form");
 };

 // ── Open edit form ──
 const openEdit = (year) => {
  // A direct click already has the full item — mark the URL-restore effect
  // as satisfied so it doesn't also redundantly re-fetch this same item.
  hasRestoredFromUrl.current = true;
  setEditingYear(year);
  setForm({
   name: year.name,
   start_date: year.start_date,
   end_date: year.end_date,
   is_active: year.is_active,
  });
  setFormError("");
  setView("form", { edit: year.id });
 };

 // Restore edit view from URL on refresh.
 const hasRestoredFromUrl = useRef(false);
 useEffect(() => {
  if (hasRestoredFromUrl.current) return;
  if (view === "form" && urlEditId) {
   hasRestoredFromUrl.current = true;
   fetchAssessmentYearById(urlEditId)
    .then((res) => openEdit(res.data))
    .catch(() => setView("list"));
  } else if (view === "form" && !urlEditId) {
   hasRestoredFromUrl.current = true;
   openCreate();
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [view, urlEditId]);

 // ── Validate form ──
 const validateForm = () => {
  if (!form.name.trim()) return "Name is required";
  if (!form.start_date) return "Start date is required";
  if (!form.end_date) return "End date is required";
  if (form.start_date >= form.end_date) return "Start date must be before end date";

  // Check duplicate name client-side
  const selfId = editingYear?.id;
  const dupName = years.find(
   (y) => y.id !== selfId && y.name.trim().toLowerCase() === form.name.trim().toLowerCase()
  );
  if (dupName) return `Assessment year with name '${form.name.trim()}' already exists`;

  // Check overlap client-side
  const overlap = years.find(
   (y) => y.id !== selfId && y.start_date < form.end_date && y.end_date > form.start_date
  );
  if (overlap) return `Date range overlaps with "${overlap.name}"`;

  return "";
 };

 // ── Save (create or update) ──
 const handleSave = async () => {
  const err = validateForm();
  if (err) { setFormError(err); return; }
  setFormError("");

  try {
   if (editingYear) {
    await updateAssessmentYear(editingYear.id, form);
    setToast({ type: "success", message: "Assessment year updated" });
    setView("list");
   } else {
    await createAssessmentYear(form);
    setToast({ type: "success", message: "Assessment year created" });
    setView("list");
   }
   loadYears();
  } catch (err) {
   const raw = err.response?.data?.detail;
   let detail = "Failed to save assessment year";
   if (typeof raw === "string") {
    detail = raw;
   } else if (Array.isArray(raw)) {
    detail = raw.map((e) => e.msg || JSON.stringify(e)).join("; ");
   }
   setFormError(detail);
   setToast({ type: "error", message: detail });
  }
 };

 // ── Open delete confirm — fetch holiday count first ──
 const openDelete = async (year) => {
  setDeleteLoading(true);
  setShowDeleteConfirm(year);
  try {
   const res = await fetchHolidays({ assessment_year_id: year.id, page_size: 1 });
   setDeleteHolidayCount(res.total || 0);
  } catch {
   setDeleteHolidayCount(0);
  } finally {
   setDeleteLoading(false);
  }
 };

 // ── Delete ──
 const handleDelete = async (id) => {
  try {
   await deleteAssessmentYear(id);
   setShowDeleteConfirm(null);
   setToast({ type: "success", message: "Assessment year deleted" });
   if (view !== "list") setView("list");
   loadYears();
  } catch (err) {
   setToast({ type: "error", message: err.response?.data?.detail || "Failed to delete assessment year" });
  }
 };

 return (
  <div className="p-6 ">
   {toast && <Toast {...toast} onClose={() => setToast(null)} />}

   {/* ── Header ── */}
   <div className="flex items-center justify-between mb-6">
    <div>
     <h1 className="text-xl font-bold text-[#24292f]">Assessment Years</h1>
     <p className="text-sm text-[#57606a] mt-1">Manage academic assessment year periods</p>
    </div>
    {canManageAssessmentYears && view === "list" && (
     <button
      onClick={openCreate}
      className="flex items-center gap-1.5 px-4 py-2 bg-[#0078d4] text-white text-xs font-medium rounded-lg hover:bg-[#106ebe] transition-all"
     >
      <HiOutlinePlus className="text-sm" /> Add Assessment Year
     </button>
    )}
   </div>

   {/* ── Table ── */}
   {view === "list" && (
   <div className="bg-white rounded-xl border border-[#e5e7eb] overflow-hidden">
    <table className="w-full text-sm">
     <thead>
      <tr className="bg-[#f6f8fa] border-b border-[#e5e7eb]">
       <th className="text-left px-4 py-3 font-semibold text-[#24292f]">Name</th>
       <th className="text-left px-4 py-3 font-semibold text-[#24292f]">Start Date</th>
       <th className="text-left px-4 py-3 font-semibold text-[#24292f]">End Date</th>
       <th className="text-left px-4 py-3 font-semibold text-[#24292f]">Status</th>
      </tr>
     </thead>
     <tbody>
      {loading ? (
       <tr>
        <td colSpan={4} className="text-center py-12 text-[#8b949e]">
         Loading…
        </td>
       </tr>
      ) : years.length === 0 ? (
       <tr>
        <td colSpan={4} className="text-center py-12 text-[#8b949e]">
         No assessment years found. Create one to get started.
        </td>
       </tr>
      ) : (
       years.map((y) => (
        <tr key={y.id} className="border-b border-[#f0f0f0] hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => openEdit(y)}>
         <td className="px-4 py-3 font-medium text-[#24292f]">{y.name}</td>
         <td className="px-4 py-3 text-[#57606a]">{y.start_date}</td>
         <td className="px-4 py-3 text-[#57606a]">{y.end_date}</td>
         <td className="px-4 py-3">
          <span
           className={`inline-block px-2 py-0.5 rounded text-[11px] font-semibold ${
            y.is_active
             ? "bg-green-100 text-green-700"
             : "bg-gray-100 text-gray-500"
           }`}
          >
           {y.is_active ? "Active" : "Inactive"}
          </span>
         </td>
        </tr>
       ))
      )}
     </tbody>
    </table>
   </div>
   )}

   {/* ═══ CREATE / EDIT FORM ═══ */}
   {view === "form" && (
    <div className="bg-white rounded-xl border border-[#e5e7eb] w-full">
     <div className="flex items-center justify-between px-5 py-4 border-b border-[#e5e7eb]">
      <div className="flex items-center gap-3">
       <button
        onClick={() => setView("list")}
        className="w-8 h-8 flex items-center justify-center rounded-md text-[#57606a] hover:bg-[#f3f4f6] transition-all border-none bg-transparent cursor-pointer"
        title="Back"
       >
        <HiOutlineChevronLeft className="text-lg" />
       </button>
       <div>
        <h3 className="text-base font-semibold text-[#24292f]">
         {!canManageAssessmentYears ? "Assessment Year Details" : editingYear ? "Edit Assessment Year" : "Add Assessment Year"}
        </h3>
        {!canManageAssessmentYears && (
         <p className="text-xs text-[#8b949e] mt-0.5">View assessment year information</p>
        )}
       </div>
      </div>
      {canManageAssessmentYears && editingYear && (
       <button onClick={() => openDelete(editingYear)} className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700">Delete</button>
      )}
     </div>
     <div className="p-5 space-y-4">
      {formError && (
       <div className="px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
        {formError}
       </div>
      )}
      <div>
       <label htmlFor="ay-name" className="block text-sm font-medium text-[#24292f] mb-1">
        Name
       </label>
       <input
        id="ay-name"
        type="text"
        placeholder="e.g. 2026-2027"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        disabled={!canManageAssessmentYears}
        className="w-full border border-[#d0d7de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078d4]/20 focus:border-[#0078d4] disabled:bg-[#f6f8fa] disabled:text-[#57606a]"
       />
      </div>
      <div className="grid grid-cols-2 gap-3">
       <div>
        <label htmlFor="ay-start" className="block text-sm font-medium text-[#24292f] mb-1">
         Start Date
        </label>
        <input
         id="ay-start"
         type="date"
         value={form.start_date}
         onChange={(e) => setForm({ ...form, start_date: e.target.value })}
         ref={(el) => { if (el && !el._noScroll) { el._noScroll = true; el.addEventListener('wheel', (ev) => ev.preventDefault(), { passive: false }); } }}
         disabled={!canManageAssessmentYears}
         className="w-full border border-[#d0d7de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078d4]/20 focus:border-[#0078d4] disabled:bg-[#f6f8fa] disabled:text-[#57606a]"
        />
       </div>
       <div>
        <label htmlFor="ay-end" className="block text-sm font-medium text-[#24292f] mb-1">
         End Date
        </label>
        <input
         id="ay-end"
         type="date"
         value={form.end_date}
         onChange={(e) => setForm({ ...form, end_date: e.target.value })}
         ref={(el) => { if (el && !el._noScroll) { el._noScroll = true; el.addEventListener('wheel', (ev) => ev.preventDefault(), { passive: false }); } }}
         disabled={!canManageAssessmentYears}
         className="w-full border border-[#d0d7de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078d4]/20 focus:border-[#0078d4] disabled:bg-[#f6f8fa] disabled:text-[#57606a]"
        />
       </div>
      </div>
      <div className="flex items-center gap-3">
       <label htmlFor="ay-active" className="relative inline-flex items-center cursor-pointer">
        <input
         id="ay-active"
         type="checkbox"
         checked={form.is_active}
         onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
         disabled={!canManageAssessmentYears}
         className="sr-only peer"
        />
        <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#0078d4]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#0078d4]" />
       </label>
       <span className="text-sm text-[#24292f]">Active</span>
      </div>
     </div>
     <div className="flex justify-end gap-2 px-5 py-4 border-t border-[#e5e7eb] bg-[#f6f8fa] rounded-b-xl">
      <button
       onClick={() => setView("list")}
       className="px-4 py-2 text-sm font-medium text-[#24292f] bg-white border border-[#d0d7de] rounded-lg hover:bg-[#f3f4f6] transition-all"
      >
       {canManageAssessmentYears ? "Cancel" : "Back"}
      </button>
      {canManageAssessmentYears && (
       <button
        onClick={handleSave}
        disabled={!form.name || !form.start_date || !form.end_date}
        className="px-4 py-2 text-sm font-medium text-white bg-[#0078d4] rounded-lg hover:bg-[#106ebe] disabled:opacity-40 transition-all"
       >
        {editingYear ? "Update" : "Create"}
       </button>
      )}
     </div>
    </div>
   )}

   {/* ═══ DELETE CONFIRM MODAL ═══ */}
   {showDeleteConfirm && (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
     <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-5 text-center">
      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-100 flex items-center justify-center">
       <HiOutlineExclamation className="text-2xl text-red-600" />
      </div>
      <h3 className="text-base font-semibold text-[#24292f] mb-1">Delete Assessment Year?</h3>
      {deleteLoading ? (
       <p className="text-sm text-[#57606a] mb-4">Checking linked holidays…</p>
      ) : (
       <p className="text-sm text-[#57606a] mb-4">
        Are you sure you want to delete <strong>{showDeleteConfirm.name}</strong>?
        {deleteHolidayCount > 0 && (
         <span className="block mt-2 text-red-600 font-medium">
          ⚠ This will also delete {deleteHolidayCount} holiday{deleteHolidayCount > 1 ? "s" : ""} linked to this year.
         </span>
        )}
       </p>
      )}
      <div className="flex justify-center gap-2">
       <button
        onClick={() => setShowDeleteConfirm(null)}
        className="px-4 py-2 text-sm font-medium text-[#24292f] bg-white border border-[#d0d7de] rounded-lg hover:bg-[#f3f4f6] transition-all"
       >
        Cancel
       </button>
       <button
        onClick={() => handleDelete(showDeleteConfirm.id)}
        disabled={deleteLoading}
        className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-40 transition-all"
       >
        Delete
       </button>
      </div>
     </div>
    </div>
   )}
  </div>
 );
}

export default AssessmentYears;
